import { ApiTranslation } from '../api-translation';
import { LocalizedText } from '../localized-text';
import { CurrencyView } from '../currency-view';
import { resolveBaseToken } from '@/client/hooks/use-market-base-token';
import { BlockLoading } from '../fetch-status/block-loading';
import { EndStatus } from '../fetch-status/end-status';
import { PositionNothing } from '../fetch-status/position-nothing';
import { FormatAfterDate } from '../format/format-after-date';
import { TextTooltip } from '../text-tooltip';
import { UploadedImage } from '../uploaded-image';
// import addLiquidityUrl from '@/client/assets/imgs/add-liquidity.png?url';
// import createPredictionUrl from '@/client/assets/imgs/create-prediction.png?url';
import depositUrl from '@/client/assets/imgs/deposit.png?url';
// import removeLiquidityUrl from '@/client/assets/imgs/remove-liquidity.png?url';
import withdrawUrl from '@/client/assets/imgs/withdraw.png?url';
import { IS_DEBUG } from '@/client/confs/app';
import { useSingedTransaction } from '@/client/hooks/use-singed-transaction';
import { useAppSettingStore } from '@/client/store/app-store';
import { cn } from '@/client/utils';
import { api } from '@/client/utils/api';
import { formatAddress } from '@/client/utils/format';
import { TransactionType, UserTransaction } from '@/libs/sdk/api';
import {
  BuyTransaction,
  ClaimMarketTransaction,
  ClaimTaskTransaction,
  DepositTransaction,
  InvitedPointsTransaction,
  SellTransaction,
  WithdrawTransaction,
} from '@/libs/sdk/api/types/transaction';
import useInViewport from 'ahooks/lib/useInViewport';
import { BasicTarget } from 'ahooks/lib/utils/domTarget';
import { useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'wouter';

type ItemProps = {
  transaction: UserTransaction;
  baseTokenSymbol: string;
};
export const WithdrawItem = ({ transaction, baseTokenSymbol }: ItemProps) => {
  const { t } = useTranslation();
  const data = useMemo(
    () => (transaction.bizDataJson ? (JSON.parse(transaction.bizDataJson) as WithdrawTransaction) : undefined),
    [transaction.bizDataJson]
  );
  if (!data) return null;
  return (
    <div className="flex items-center space-x-4 p-4">
      <img src={withdrawUrl} className="size-8 rounded-full" />
      <div className="flex flex-col flex-1 justify-center items-stretch w-full gap-1">
        <div className="flex items-center justify-between text-sm font-medium">
          <div>{t('withdraw')}</div>
          <div className="space-x-1">
            -<CurrencyView value={transaction.amount} decimal={transaction.decimal} />
            <span className="text-foreground/40">{baseTokenSymbol}</span>
          </div>
        </div>
        <div className="flex items-center justify-between text-xs leading-[normal] text-foreground/70">
          <div className="flex items-center whitespace-nowrap">
            {t('sent-to')}
            <TextTooltip tooltip={data.address} show={true} className="ml-1">
              {formatAddress(data.address)}
            </TextTooltip>
          </div>
          <div>
            <FormatAfterDate time={transaction.timestamp} />
          </div>
        </div>
      </div>
    </div>
  );
};

export const DepositItem = ({ transaction, baseTokenSymbol }: ItemProps) => {
  const { t } = useTranslation();
  const data = useMemo(
    () =>
      transaction.bizDataJson
        ? (JSON.parse(transaction.bizDataJson) as DepositTransaction)
        : ({} as Partial<DepositTransaction>),
    [transaction.bizDataJson]
  );
  return (
    <div className="flex items-center space-x-4 p-4 ">
      <img src={depositUrl} className="size-8 rounded-full" />
      <div className="flex flex-col flex-1 justify-center items-stretch w-full gap-1">
        <div className="flex items-center justify-between text-sm font-medium">
          <div>{t('deposit')}</div>
          <div className="space-x-1 text-beneficial">
            <CurrencyView isChange={true} value={transaction.amount} decimal={transaction.decimal} />
            <span className="text-foreground/40">{baseTokenSymbol}</span>
          </div>
        </div>
        <div className="flex items-center justify-between text-xs leading-[normal] text-foreground/70">
          <div className="flex items-center whitespace-nowrap">
            {t('received-from')}
            <TextTooltip tooltip={data.address} show={true} className="ml-1">
              {formatAddress(data.address)}
            </TextTooltip>
          </div>
          <div>
            <FormatAfterDate time={transaction.timestamp} />
          </div>
        </div>
      </div>
    </div>
  );
};

// export const RemoveLiquidityItem = ({ transaction }: ItemProps) => {
//   return (
//     <div className="flex items-center space-x-4 p-4 ">
//       <img src={removeLiquidityUrl} className="size-8 rounded-full" />
//       <div className="flex flex-col flex-1 justify-center items-stretch w-full gap-1">
//         <div className="flex items-center justify-between text-sm font-medium">
//           <div>Remove Liquidity</div>
//           <div className="space-x-1">
//             <span className="text-destructive">+613</span>
//             <span className="text-foreground/40">USDC</span>
//           </div>
//         </div>
//         <div className="flex items-center justify-between text-sm leading-[normal] text-foreground/70">
//           <TextTooltip tooltip="Circle IPO in 2025?" className="line-clamp-1">
//             Circle IPO in 2025?
//           </TextTooltip>
//           <div>1h ago</div>
//         </div>
//       </div>
//     </div>
//   );
// };

// export const AddLiquidityItem = ({ transaction }: ItemProps) => {
//   return (
//     <div className="flex items-center space-x-4 p-4 ">
//       <img src={addLiquidityUrl} className="size-8 rounded-full" />
//       <div className="flex flex-col flex-1 justify-center items-stretch w-full gap-1">
//         <div className="flex items-center justify-between text-sm font-medium">
//           <div>Add Liquidity</div>
//           <div className="space-x-1">
//             <span>-613</span>
//             <span className="text-foreground/40">USDC</span>
//           </div>
//         </div>
//         <div className="flex items-center justify-between text-sm leading-[normal] text-foreground/70">
//           <div>Circle IPO in 2025?</div>
//           <div>1h ago</div>
//         </div>
//       </div>
//     </div>
//   );
// };

// export const CreatePredictionItem = ({ transaction }: ItemProps) => {
//   return (
//     <div className="flex items-center space-x-4 p-4 ">
//       <img src={createPredictionUrl} className="size-8 rounded-full" />
//       <div className="flex flex-col flex-1 justify-center items-stretch w-full gap-1">
//         <div className="flex items-center justify-between text-sm font-medium">
//           <div>Create Prediction</div>
//           <div className="space-x-1">
//             <span>-613</span>
//             <span className="text-foreground/40">Point</span>
//           </div>
//         </div>
//         <div className="flex items-center justify-between text-sm leading-[normal] text-foreground/70">
//           <TextTooltip tooltip="Will OpenAI launch GPT-5 before Q1 2026?" className="line-clamp-1">
//             Will OpenAI launch GPT-5 before Q1 2026?
//           </TextTooltip>
//           <div className="whitespace-nowrap">1h ago</div>
//         </div>
//       </div>
//     </div>
//   );
// };

export const ClaimMarketItem = ({ transaction, baseTokenSymbol }: ItemProps) => {
  const { t } = useTranslation();
  const [, navigator] = useLocation();
  const data = useMemo(
    () => (transaction.bizDataJson ? (JSON.parse(transaction.bizDataJson) as ClaimMarketTransaction) : undefined),
    [transaction.bizDataJson]
  );
  if (!data) return null;
  return (
    <div
      className="flex items-center space-x-4 p-4 [&_h3]:market-title-hover"
      onClick={() => navigator(`/market/${data.marketAddress}`)}
    >
      <UploadedImage fileKey={data.marketPicUrl} className="size-8 rounded-full" />
      <div className="flex flex-col flex-1 justify-center items-stretch w-full gap-1">
        <div className="flex items-center justify-between text-sm font-medium">
          <div>{t('redeem')}</div>
          <div className="space-x-1 text-beneficial">
            <CurrencyView isChange={true} value={transaction.amount} decimal={transaction.decimal} />
            <span className="text-foreground/40">{baseTokenSymbol}</span>
          </div>
        </div>
        <div className="flex items-center justify-between text-xs leading-[normal] text-foreground/70">
          <TextTooltip tooltip={data.marketName} className="line-clamp-3">
            <LocalizedText i18nMap={data.nameI18n} fallback={data.marketName} />
          </TextTooltip>
          <div className="whitespace-nowrap">
            <FormatAfterDate time={transaction.timestamp} />
          </div>
        </div>
      </div>
    </div>
  );
};

export const SellItem = ({ transaction, baseTokenSymbol }: ItemProps) => {
  const { t } = useTranslation();
  const [, navigator] = useLocation();
  const data = useMemo(
    () => (transaction.bizDataJson ? (JSON.parse(transaction.bizDataJson) as SellTransaction) : undefined),
    [transaction.bizDataJson]
  );
  if (!data) return null;
  return (
    <div
      className="flex items-center space-x-4 p-4 [&_h3]:market-title-hover"
      onClick={() => navigator(`/market/${data.marketAddress}`)}
    >
      <UploadedImage fileKey={data.marketPicUrl} className="size-8 rounded-full" />
      <div className="flex flex-col flex-1 justify-center items-stretch w-full gap-1">
        <div className="flex items-center justify-between text-sm font-medium">
          <div>{t('sell')}</div>
          <div className="space-x-1 text-beneficial">
            <CurrencyView isChange={true} value={transaction.amount} decimal={transaction.decimal} />
            <span className="text-foreground/40">{baseTokenSymbol}</span>
          </div>
        </div>
        <div className="flex items-center justify-between text-xs leading-[normal] text-foreground/70">
          <TextTooltip tooltip={data.marketName} className="line-clamp-3">
            <LocalizedText i18nMap={data.nameI18n} fallback={data.marketName} />
          </TextTooltip>
          <div className="whitespace-nowrap">
            <FormatAfterDate time={transaction.timestamp} />
          </div>
        </div>
      </div>
    </div>
  );
};

export const BuyItem = ({ transaction, baseTokenSymbol }: ItemProps) => {
  const { t } = useTranslation();
  const [, navigator] = useLocation();
  const data = useMemo(
    () => (transaction.bizDataJson ? (JSON.parse(transaction.bizDataJson) as BuyTransaction) : undefined),
    [transaction.bizDataJson]
  );
  if (!data) return null;
  return (
    <div
      className="flex items-center space-x-4 p-4 [&_h3]:market-title-hover"
      onClick={() => navigator(`/market/${data.marketAddress}`)}
    >
      <UploadedImage fileKey={data?.marketPicUrl} className="size-8 rounded-full" />
      <div className="flex flex-col flex-1 justify-center items-stretch w-full gap-1">
        <div className="flex items-center justify-between text-sm font-medium">
          <div>{t('buy')}</div>
          <div className="space-x-1">
            -<CurrencyView value={transaction.amount} decimal={transaction.decimal} />
            <span className="text-foreground/40">{baseTokenSymbol}</span>
          </div>
        </div>
        <div className="flex items-center justify-between text-xs leading-[normal] text-foreground/70">
          <TextTooltip tooltip={data.marketName} className="line-clamp-3">
            <LocalizedText i18nMap={data.nameI18n} fallback={data.marketName} />
          </TextTooltip>
          <div className="whitespace-nowrap">
            <FormatAfterDate time={transaction.timestamp} />
          </div>
        </div>
      </div>
    </div>
  );
};

export const InvitedItem = ({ transaction, baseTokenSymbol }: ItemProps) => {
  const { t } = useTranslation();
  const data = useMemo(
    () => (transaction.bizDataJson ? (JSON.parse(transaction.bizDataJson) as InvitedPointsTransaction) : undefined),
    [transaction.bizDataJson]
  );
  if (!data) return null;
  return (
    <div className="flex items-center space-x-4 p-4">
      <img src="/img/dpin-badge.png" alt="DPIN" className="size-8 flex-shrink-0 object-contain" />
      <div className="space-y-1 flex-1">
        <div className="flex justify-between items-center text-sm font-medium leading-5">
          <div className="flex-1">{t('invite-rewards')}</div>
          <div className="flex gap-1 text-beneficial">
            <CurrencyView isChange={true} value={transaction.amount} decimal={transaction.decimal} />
            <span className="text-foreground/40">{baseTokenSymbol}</span>
          </div>
        </div>
        <div className="flex items-center justify-between text-xs leading-[normal] text-foreground/70">
          <a href={`/user/${data.invitedUid}`} className="line-clamp-1 hover:underline">
            @{data.invitedName}
          </a>
          <FormatAfterDate time={transaction.timestamp} />
        </div>
      </div>
    </div>
  );
};

export const TaskItem = ({ transaction, baseTokenSymbol }: ItemProps) => {
  const { t } = useTranslation();
  const data = useMemo(
    () => (transaction.bizDataJson ? (JSON.parse(transaction.bizDataJson) as ClaimTaskTransaction) : undefined),
    [transaction.bizDataJson]
  );
  if (!data) return null;
  return (
    <div className="flex items-center space-x-4 p-4">
      <img src="/img/dpin-badge.png" alt="DPIN" className="size-8 flex-shrink-0 object-contain" />
      <div className="space-y-1 flex-1">
        <div className="flex justify-between items-center text-sm font-medium leading-5">
          <div className="flex-1">{t('task-rewards')}</div>
          <div className="flex gap-1 text-beneficial">
            <CurrencyView value={data.reward} decimal={0} isChange={true} />
            <span className="text-foreground/40 font-400">{baseTokenSymbol}</span>
          </div>
        </div>
        <div className="flex items-center justify-between text-xs leading-[normal] text-foreground/70 ">
          <span className="text-foreground/70 font-normal">
            <ApiTranslation>{data.taskName}</ApiTranslation>
          </span>
          <FormatAfterDate time={transaction.timestamp} />
        </div>
      </div>
    </div>
  );
};

export const InitialItem = ({ transaction, baseTokenSymbol }: ItemProps) => {
  const { t } = useTranslation();
  return (
    <div className="flex items-center space-x-4 p-4">
      <img src="/img/dpin-badge.png" alt="DPIN" className="size-8 flex-shrink-0 object-contain" />
      <div className="space-y-1 flex-1">
        <div className="flex justify-between items-center text-sm font-medium leading-5">
          <div className="flex-1">{t('task-rewards')}</div>
          <div className="flex gap-1 text-beneficial">
            <CurrencyView isChange={true} value={transaction.amount} decimal={transaction.decimal} />
            <span className="text-foreground/40 font-400">{baseTokenSymbol}</span>
          </div>
        </div>
        <div className="flex items-center justify-between text-xs leading-[normal] text-foreground/70 ">
          <span className="text-foreground/70 font-normal">{t('bonus-for-your-sign-up')}</span>
          <FormatAfterDate time={transaction.timestamp} />
        </div>
      </div>
    </div>
  );
};

type ItemContentProps = {
  type: TransactionType;
  transaction: UserTransaction;
  baseTokenSymbol: string;
};
const ItemContent = ({ type, transaction, baseTokenSymbol: fallbackSymbol }: ItemContentProps) => {
  // Mixed multi-base history: each row is denominated in its own token
  const baseTokenSymbol = resolveBaseToken(transaction.tokenAddress)?.symbol ?? fallbackSymbol;
  switch (type) {
    case TransactionType.buy:
      return <BuyItem transaction={transaction} baseTokenSymbol={baseTokenSymbol} />;
    case TransactionType.sell:
      return <SellItem transaction={transaction} baseTokenSymbol={baseTokenSymbol} />;
    case TransactionType.claimMarket:
      return <ClaimMarketItem transaction={transaction} baseTokenSymbol={baseTokenSymbol} />;
    case TransactionType.initPoints:
      return <InitialItem transaction={transaction} baseTokenSymbol={baseTokenSymbol} />;
    case TransactionType.invitePoints:
      return <InvitedItem transaction={transaction} baseTokenSymbol={baseTokenSymbol} />;
    case TransactionType.deposit:
      return <DepositItem transaction={transaction} baseTokenSymbol={baseTokenSymbol} />;
    case TransactionType.withdraw:
      return <WithdrawItem transaction={transaction} baseTokenSymbol={baseTokenSymbol} />;
    case TransactionType.claimTask:
      return <TaskItem transaction={transaction} baseTokenSymbol={baseTokenSymbol} />;
    default:
      if (IS_DEBUG) console.log('unknown transaction type', type);
      return null;
  }
};

type TransactionItemProps = {
  transaction: UserTransaction;
  baseTokenSymbol: string;
  className?: string;
  root?: BasicTarget<Element>;
  onViewChange?: (isInView: boolean) => void;
};

export const TransactionItem = ({ transaction, baseTokenSymbol, root, onViewChange }: TransactionItemProps) => {
  const ref = useRef<HTMLDivElement>(null);
  useInViewport(ref, {
    root,
    callback: entry => {
      onViewChange?.(entry.isIntersecting);
    },
  });
  return (
    <div ref={ref} className="[&:first-child]:rounded-t-4 [&:last-child]:rounded-b-4 cursor-pointer hover:bg-foreground/10">
      <ItemContent type={transaction.type} transaction={transaction} baseTokenSymbol={baseTokenSymbol} />
    </div>
  );
};

type TransactionProps = {
  uid: string;
};

export const Transaction = ({ uid }: TransactionProps) => {
  const { t } = useTranslation();
  const baseToken = useAppSettingStore(state => state.baseToken);
  const { data, isLoading, hasNextPage, isFetchingNextPage, fetchNextPage } = useSingedTransaction(api, {
    baseTokenAddress: baseToken.address,
  });
  const transactionList = useMemo(() => data?.pages.flatMap(item => item.transactions) ?? [], [data]);
  const moreLastIndex = useMemo(() => transactionList.length - 3, [transactionList.length]);
  const loadMoreItems = async (isInView: boolean, index: number) => {
    if (isInView && index === moreLastIndex && hasNextPage && !isFetchingNextPage) {
      if (IS_DEBUG) console.log('load more transaction', moreLastIndex, isInView, index);
      await fetchNextPage();
    }
  };
  if (!uid) return null;
  return (
    <div className="w-full">
      <div
        className={cn(
          'w-full divide-y divide-foreground/10 rounded-4 bg-card',
          transactionList.length > 0 && 'border border-foreground/10'
        )}
      >
        {transactionList.map((item, index) => (
          <TransactionItem
            key={`transaction-${item.timestamp}`}
            transaction={item}
            baseTokenSymbol={t(baseToken.translateKey)}
            onViewChange={inView => loadMoreItems(inView, index)}
          />
        ))}
      </div>
      {(isLoading || isFetchingNextPage) && <BlockLoading />}
      {!isLoading && !hasNextPage && transactionList.length === 0 && <PositionNothing />}
      {!isLoading && !hasNextPage && transactionList.length > 0 && <EndStatus />}
    </div>
  );
};

export default Transaction;
